/* eslint-disable no-const-assign */
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Dropdown, DropdownMenu, DropdownToggle, Badge } from "reactstrap";

//import images
import logoSm from "../assets/images/logo-sm.png";
import logoDark from "../assets/images/logo-dark.png";
import logoLight from "../assets/images/logo-light.png";

//import Components
import FullScreenDropdown from "../Components/Common/FullScreenDropdown";
import ProfileDropdown from "../Components/Common/ProfileDropdown";

import {
  fetchNotifications,
  markNotificationAsRead,
  changeSidebarVisibility,
  loadMoreNotifications
} from "../slices/thunks";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";

// eslint-disable-next-line react/prop-types
const Header = ({ headerClass }) => {
  const dispatch = useDispatch();
  const dropdownRef = useRef(null);
  const observerRef = useRef(null);

  const selectDashboardData = createSelector(
    (state) => state.Layout.sidebarVisibilitytype,
    (sidebarVisibilitytype) => sidebarVisibilitytype
  );

  // Notification selector
  const selectNotifications = createSelector(
    (state) => state.Notification,
    (notificationState) => ({
      notifications: notificationState.notifications,
      unreadCount: notificationState.unreadCount,
      loading: notificationState.loading,
      loadingMore: notificationState.loadingMore,
      error: notificationState.error,
      pagination: notificationState.pagination,
      hasMore: notificationState.hasMore
    })
  );

  // Inside your component
  const sidebarVisibilitytype = useSelector(selectDashboardData);
  const { 
    notifications, 
    unreadCount, 
    loading, 
    loadingMore,
    pagination,
    hasMore 
  } = useSelector(selectNotifications);

  const [notificationDropdown, setNotificationDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const toggleNotificationDropdown = () => {
    setNotificationDropdown(!notificationDropdown);
    // Reset to page 1 when dropdown opens
    if (!notificationDropdown) {
      setCurrentPage(1);
    }
  };

  // Format time ago
  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';

    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diffInSeconds = Math.floor((now - notificationTime) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} min ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    return `${Math.floor(diffInSeconds / 86400)} days ago`;
  };

  // Handle notification item click - mark as read and remove highlight
  const handleNotificationItemClick = async (notification, e) => {
    e.stopPropagation();

    if (!notification || !notification._id) {
      console.error('Invalid notification object:', notification);
      return;
    }

    // Only mark as read if it's unread
    if (!notification.isRead) {
      try {
        console.log('Marking notification as read:', notification._id);
        await dispatch(markNotificationAsRead(notification._id)).unwrap();
        console.log('Successfully marked notification as read');
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }
  };

  // Load more notifications when scrolling to bottom
  const handleScroll = useCallback(() => {
    if (!dropdownRef.current || loadingMore || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = dropdownRef.current;
    
    // Load more when 100px from bottom
    if (scrollHeight - scrollTop - clientHeight < 100) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      dispatch(loadMoreNotifications(nextPage));
    }
  }, [loadingMore, hasMore, currentPage, dispatch]);

  // Set up intersection observer for lazy loading
  const lastNotificationElementRef = useCallback((node) => {
    if (loadingMore) return;
    if (observerRef.current) observerRef.current.disconnect();
    
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loadingMore) {
        const nextPage = currentPage + 1;
        setCurrentPage(nextPage);
        dispatch(loadMoreNotifications(nextPage));
      }
    });
    
    if (node) observerRef.current.observe(node);
  }, [loadingMore, hasMore, currentPage, dispatch]);

  useEffect(() => {
    // Fetch notifications when component mounts
    dispatch(fetchNotifications(1));

    // Optional: Set up polling to refresh notifications periodically
    const interval = setInterval(() => {
      dispatch(fetchNotifications(1)); // Always refresh first page
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [dispatch]);

  // Add scroll event listener when dropdown is open
  useEffect(() => {
    const dropdownElement = dropdownRef.current;
    if (dropdownElement && notificationDropdown) {
      dropdownElement.addEventListener('scroll', handleScroll);
      return () => dropdownElement.removeEventListener('scroll', handleScroll);
    }
  }, [notificationDropdown, handleScroll]);

  const toogleMenuBtn = () => {
    const windowSize = document.documentElement.clientWidth;
    dispatch(changeSidebarVisibility("show"));

    if (windowSize > 767)
      document.querySelector(".hamburger-icon").classList.toggle("open");

    //For collapse horizontal menu
    if (document.documentElement.getAttribute("data-layout") === "horizontal") {
      document.body.classList.contains("menu")
        ? document.body.classList.remove("menu")
        : document.body.classList.add("menu");
    }

    //For collapse vertical and semibox menu
    if (
      sidebarVisibilitytype === "show" &&
      (document.documentElement.getAttribute("data-layout") === "vertical" ||
        document.documentElement.getAttribute("data-layout") === "semibox")
    ) {
      if (windowSize < 1025 && windowSize > 767) {
        document.body.classList.remove("vertical-sidebar-enable");
        document.documentElement.getAttribute("data-sidebar-size") === "sm"
          ? document.documentElement.setAttribute("data-sidebar-size", "")
          : document.documentElement.setAttribute("data-sidebar-size", "sm");
      } else if (windowSize > 1025) {
        document.body.classList.remove("vertical-sidebar-enable");
        document.documentElement.getAttribute("data-sidebar-size") === "lg"
          ? document.documentElement.setAttribute("data-sidebar-size", "sm")
          : document.documentElement.setAttribute("data-sidebar-size", "lg");
      } else if (windowSize <= 767) {
        document.body.classList.add("vertical-sidebar-enable");
        document.documentElement.setAttribute("data-sidebar-size", "lg");
      }
    }

    //Two column menu
    if (document.documentElement.getAttribute("data-layout") === "twocolumn") {
      document.body.classList.contains("twocolumn-panel")
        ? document.body.classList.remove("twocolumn-panel")
        : document.body.classList.add("twocolumn-panel");
    }
  };

  return (
    <React.Fragment>
      <header id="page-topbar" className={headerClass}>
        <div className="layout-width">
          <div className="navbar-header">
            <div className="d-flex">
              <div className="navbar-brand-box horizontal-logo">
                <Link to="/dashboard" className="logo logo-dark">
                  <span className="logo-sm">
                    <img src={logoSm} alt="" height="30" />
                  </span>
                  <span className="logo-lg">
                    <img src={logoDark} alt="" height="40" />
                  </span>
                </Link>

                <Link to="/dashboard" className="logo logo-light">
                  <span className="logo-sm">
                    <img src={logoSm} alt="" height="30" />
                  </span>
                  <span className="logo-lg">
                    <img src={logoLight} alt="" height="40" />
                  </span>
                </Link>
              </div>

              <button
                onClick={toogleMenuBtn}
                type="button"
                className="btn btn-sm px-3 fs-16 header-item vertical-menu-btn topnav-hamburger"
                id="topnav-hamburger-icon"
              >
                <span className="hamburger-icon">
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
              </button>
            </div>

            <div className="d-flex align-items-center">
              {/* Notification Dropdown */}
              <Dropdown
                isOpen={notificationDropdown}
                toggle={toggleNotificationDropdown}
                className="topbar-head-dropdown header-item"
              >
                <DropdownToggle
                  type="button"
                  tag="button"
                  className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle position-relative"
                >
                  <i className="bx bx-bell fs-22"></i>
                  {unreadCount > 0 && (
                    <Badge color="danger" className="position-absolute translate-middle badge rounded-pill" style={{ top: '12px', right: '12px', fontSize: '0.6em', padding: '2px 4px' }}>
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </Badge>
                  )}
                </DropdownToggle>
                <DropdownMenu className="dropdown-menu-lg dropdown-menu-end p-0">
                  <div className="p-3 border-bottom">
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">Notifications</h6>
                      {pagination && (
                        <small className="text-muted">
                          Page {pagination.currentPage} of {pagination.totalPages}
                        </small>
                      )}
                    </div>
                  </div>
                  <div 
                    ref={dropdownRef}
                    style={{ maxHeight: '300px', overflowY: 'auto' }}
                  >
                    {loading && currentPage === 1 ? (
                      <div className="p-3 text-center text-muted">
                        <div className="spinner-border spinner-border-sm me-2" />
                        Loading notifications...
                      </div>
                    ) : notifications.length > 0 ? (
                      <>
                        {notifications.map((notification, index) => (
                          <div
                            key={notification._id}
                            ref={index === notifications.length - 1 ? lastNotificationElementRef : null}
                            className={`p-3 border-bottom ${!notification.isRead ? 'notifiction-bg' : ''}`}
                            onClick={(e) => handleNotificationItemClick(notification, e)}
                            style={{ cursor: 'pointer', transition: 'background-color 0.2s ease' }}
                          >
                            <div className="d-flex align-items-start">
                              <div className="flex-grow-1">
                                <h6 className="mb-1">{notification.title || 'Notification'}</h6>
                                <p className="mb-0 text-muted">{notification.message}</p>
                                <small className="text-muted">
                                  {formatTimeAgo(notification.createdAt)}
                                </small>
                              </div>
                            </div>
                          </div>
                        ))}
                        
                        {/* Loading more indicator */}
                        {loadingMore && (
                          <div className="p-3 text-center text-muted">
                            <div className="spinner-border spinner-border-sm me-2" />
                            Loading more notifications...
                          </div>
                        )}
                        
                        {/* No more notifications message */}
                        {!hasMore && notifications.length > 0 && (
                          <div className="p-3 text-center text-muted">
                            <small>No more notifications</small>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="p-3 text-center text-muted">
                        No notifications
                      </div>
                    )}
                  </div>
                </DropdownMenu>
              </Dropdown>

              <FullScreenDropdown />
              <ProfileDropdown />
            </div>
          </div>
        </div>
      </header>
    </React.Fragment>
  );
};

export default Header;